/**
 * Repo: PWT.PlattformUI
 * Component: <List> (default exported), <SearchListItemWrapper> (local)
 * @description:
 *  A list that displays search results.
 *  Can be used with a custom @property SearchListItem component.
 *  or the default <ListItem> component.
 */
import classNames from 'classnames';
import PropTypes from 'prop-types';
import React from 'react';
import ListItem from './listitem';

const SearchListItemWrapper = ({ CustomSearchListItem, ...props }) => {
  const theme = classNames({
    'c-related__item': true,
    'c-related__item--is-search': true,
    'c-related__item--has-image': props.Image,
    [`c-related__item--theme-${props.Theme}`]: true,
  });

  const lastItem = props.isLast ? (
    <span id="search-list-last" tabIndex="-1" />
  ) : null;

  //Shows the revised component if there is a isNew prop
  let showRevised;
  if (props.IsNew !== undefined && props.IsNew !== null) {
    if (props.EventDate) {
      showRevised = true;
    }
  } else {
    showRevised = false;
  }
  const trackingParams = {
    HitId: props.HitId,
    query: props.query,
    TrackId: props.TrackId,
    DisableClickTracking: props.DisableClickTracking,
  };
  return (
    <li>
      {CustomSearchListItem ? (
        <CustomSearchListItem {...trackingParams} {...props} />
      ) : (
        <ListItem
          className={theme}
          revisedAsPublished={props.revisedAsPublished}
          isNew={props.IsNew}
          showRevised={showRevised}
          eventDate={props.EventDate}
          href={props.Url}
          searchHitHeading={props.SearchHitHeading}
          image={props.Image}
          vignette={props.Vignette}
          heading={props.Heading}
          text={props.Text}
          score={props.Score}
          translations={props.translations}
          subjectHeading={props.SubjectHeading}
          totalHits={props.TotalHits}
          informationtype={props.InformationType}
          selectionCode={props.SelectionCode}
          vardpersonal1177={props.vardpersonal1177}
          primaryCareUrl={props.PrimaryCareUrl}
          specializedCareUrl={props.SpecializedCareUrl}
          isExternalSearchHit={props?.isExternalSearchHit}
          regionalizeSearchHits={props?.regionalizeSearchHits}
          availableLanguagesHeader={props?.availableLanguagesHeader}
          availableLanguages={props?.AvailableLanguages}
          {...trackingParams}
        />
      )}
      {lastItem}
    </li>
  );
};

const List = ({
  SearchHits,
  TotalHits,
  children,
  TrackId,
  query,
  translations,
  revisedAsPublished,
  DisableClickTracking,
  CustomSearchListItem = null,
  vardpersonal1177,
  isExternalSearchHit,
  regionalizeSearchHits,
  availableLanguagesHeader,
}) => {
  if (!SearchHits) return null;
  const items = SearchHits.map((item, index) => (
    <SearchListItemWrapper
      translations={translations}
      revisedAsPublished={revisedAsPublished}
      key={index}
      query={query}
      TotalHits={TotalHits}
      DisableClickTracking={DisableClickTracking}
      TrackId={TrackId}
      {...item}
      isLast={index + 1 === SearchHits.length}
      CustomSearchListItem={CustomSearchListItem}
      vardpersonal1177={vardpersonal1177}
      isExternalSearchHit={isExternalSearchHit}
      regionalizeSearchHits={regionalizeSearchHits}
      availableLanguagesHeader={availableLanguagesHeader}
      availableLanguages={item?.AvailableLanguages}
    />
  ));

  return (
    <div className="c-searchlist" id="search">
      <ul
        className="c-related"
        aria-live="polite"
        data-cy="ui-searchform-resultlist"
      >
        {items}
      </ul>

      <div className="c-searchlist__footer">{children}</div>
    </div>
  );
};

List.propTypes = {
  /** (BE) Search block translations */
  translations: PropTypes.object,
  /** (BE) */
  ResultText: PropTypes.string,
  /** (BE) */
  SearchHits: PropTypes.array,
  /** (BE) */
  TrackId: PropTypes.string,
  /** (BE) */
  TotalHits: PropTypes.number,
  /** (BE) */
  EmptySearchResultText: PropTypes.string,
  /** (BE) List of block images */
  SuggestionText: PropTypes.shape({
    Query: PropTypes.string,
    Text: PropTypes.string,
    Url: PropTypes.string,
  }),
  /** (BE) Available languages */
  availableLanguagesHeader: PropTypes.string,
  /** (BE) */
  AvailableLanguages: PropTypes.array,
  query: PropTypes.string,
  hideSearchHeading: PropTypes.bool,
  /** List item component */
  CustomSearchListItem: PropTypes.any,
  vardpersonal1177: PropTypes.bool,
};

export default List;
